import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EquipmentCard from '../components/EquipmentCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Filter, ArrowLeft, Package, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BrowseEquipment = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    if (!BACKEND_URL) {
      setError('Backend URL not configured');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/equipment`);
      setEquipment(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch equipment:', err);
      setError('Failed to load equipment');
      setEquipment([]);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from equipment
  const categories = [...new Set((equipment || []).map(item => item?.category).filter(Boolean))];

  const filteredEquipment = (equipment || []).filter(item => {
    if (!item) return false;
    const matchesSearch = item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase() || '') ||
                         (item?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase() || ''));
    const matchesCategory = categoryFilter === 'all' || item?.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading equipment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchEquipment} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={() => navigate('/browse')}
          data-testid="back-to-browse-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to PA Systems
        </Button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-white mb-4 flex items-center gap-3" data-testid="equipment-page-title">
            <Package className="h-10 w-10 text-yellow-500" />
            Browse Equipment
          </h1>
          <p className="text-xl text-gray-400">
            Rent individual audio equipment for your specific needs
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4 md:space-y-0 md:flex md:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Search equipment..."
              className="pl-10 py-6 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="equipment-search-input"
            />
          </div>
          <div className="md:w-64">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="py-6 bg-gray-800 border-gray-700 text-white" data-testid="equipment-category-filter">
                <Filter className="h-4 w-4 mr-2 text-yellow-500" />
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all" className="text-white hover:bg-gray-700">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat} className="text-white hover:bg-gray-700">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-400">
            Showing <span className="font-semibold text-yellow-500">{filteredEquipment.length}</span> equipment item{filteredEquipment.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Equipment Grid */}
        {filteredEquipment.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="equipment-grid">
            {filteredEquipment.map(item => (
              <EquipmentCard key={item.equipment_id} equipment={item} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Package className="h-16 w-16 text-gray-700 mx-auto mb-4" />
            <p className="text-xl text-gray-500">No equipment found matching your criteria</p>
            <Button
              className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseEquipment;
